import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion, type PanInfo } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { CHAT_THREAD_VISUALS } from './chatThreadVisuals';
import { useHomeRedesignStrings } from './useHomeRedesignStrings';

function avatarFace(src: string) {
  return (
    <img
      src={src}
      alt=""
      width={28}
      height={28}
      loading="lazy"
      decoding="async"
      className="size-7 shrink-0 rounded-full object-cover ring-2 ring-white shadow-[0_1px_3px_rgba(74,33,71,0.12)]"
      aria-hidden
    />
  );
}

type Thread = {
  threadTitle: string;
  threadSub: string;
  msg1: string;
  msg2: string;
  msg3: string;
  msg4: string;
};

function ChatThreadCard({
  thread,
  threadIdx,
  contentMinHeightPx,
}: {
  thread: Thread;
  threadIdx: number;
  /** Tallest card (px) so the stack stays aligned without empty filler. */
  contentMinHeightPx?: number;
}) {
  const v = CHAT_THREAD_VISUALS[threadIdx] ?? CHAT_THREAD_VISUALS[0];

  return (
    <div
      className="flex w-full flex-col rounded-[28px] border border-[rgba(74,33,71,0.1)] bg-white p-5 shadow-[0px_24px_28px_rgba(74,33,71,0.08)] sm:p-6 lg:p-6"
      style={contentMinHeightPx ? { minHeight: contentMinHeightPx } : undefined}
    >
      <div className="flex shrink-0 items-center gap-2.5 border-b border-[rgba(74,33,71,0.1)] pb-3 sm:gap-3 sm:pb-3.5">
        <img
          src={v.cover}
          alt=""
          width={44}
          height={44}
          loading="lazy"
          decoding="async"
          className="size-10 shrink-0 rounded-full object-cover ring-2 ring-[rgba(74,33,71,0.08)] sm:size-11"
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <p className="font-jakarta text-[15px] font-bold leading-snug text-[#2a0f2a] sm:text-base">{thread.threadTitle}</p>
          <p className="font-jakarta text-[12.5px] leading-snug text-[#5a3158] sm:text-[13px]">{thread.threadSub}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-2.5 sm:mt-3.5 sm:gap-3">
        <div className="flex items-end gap-2">
          {avatarFace(v.faces.m)}
          <div className="max-w-[85%] rounded-[20px] rounded-bl-md bg-[#f7eff4] px-3.5 py-2 font-jakarta text-[14.5px] leading-snug text-[#2a0f2a] sm:rounded-[22px] sm:px-4 sm:text-[15px]">
            {thread.msg1}
          </div>
        </div>
        <div className="flex items-end gap-2">
          {avatarFace(v.faces.l)}
          <div className="rounded-[20px] rounded-bl-md bg-[#f7eff4] px-3.5 py-2 font-jakarta text-[14.5px] leading-snug text-[#2a0f2a] sm:rounded-[22px] sm:px-4 sm:text-[15px]">
            {thread.msg2}
          </div>
        </div>
        <div className="flex justify-end pl-12 sm:pl-16">
          <div className="rounded-[20px] rounded-br-md bg-chisme-primary px-3.5 py-2 font-jakarta text-[14.5px] leading-snug text-white sm:rounded-[22px] sm:px-4 sm:text-[15px]">
            {thread.msg3}
          </div>
        </div>
        <div className="flex items-end gap-2">
          {avatarFace(v.faces.a)}
          <div className="rounded-[20px] rounded-bl-md bg-[#f7eff4] px-3.5 py-2 font-jakarta text-[14.5px] leading-snug text-[#2a0f2a] sm:rounded-[22px] sm:px-4 sm:text-[15px]">
            {thread.msg4}
          </div>
        </div>
      </div>
    </div>
  );
}

const SWIPE_PX = 52;
const SWIPE_V = 420;

const STACK_STEP_Y = 14;

const HomeRedesignChat: React.FC = () => {
  const c = useHomeRedesignStrings().chat;
  const threads = c.threads;
  const n = threads.length;
  const { currentLanguage } = useLanguage();

  const reduceMotion = useReducedMotion();
  const [order, setOrder] = useState(() => Array.from({ length: n }, (_, i) => i));
  const [cardContentMinPx, setCardContentMinPx] = useState<number | undefined>(undefined);
  const measureRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = measureRef.current;
    if (!root) return undefined;

    const measure = () => {
      let max = 0;
      for (const child of Array.from(root.children)) {
        max = Math.max(max, (child as HTMLElement).offsetHeight);
      }
      setCardContentMinPx(max > 0 ? max : undefined);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [threads, currentLanguage, n]);

  const paintOrder = useMemo(
    () => Array.from({ length: n }, (_, i) => i).sort((a, b) => order.indexOf(b) - order.indexOf(a)),
    [order, n],
  );

  const sendFrontToBack = useCallback(() => {
    setOrder(([first, ...rest]) => [...rest, first]);
  }, []);

  const bringBackToFront = useCallback(() => {
    setOrder((prev) => {
      const last = prev[prev.length - 1];
      return [last, ...prev.slice(0, -1)];
    });
  }, []);

  const onDragEnd = useCallback(
    (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo, depth: number) => {
      if (depth !== 0 || reduceMotion) return;
      const x = info.offset.x;
      const vx = info.velocity.x;
      if (x < -SWIPE_PX || vx < -SWIPE_V) sendFrontToBack();
      else if (x > SWIPE_PX || vx > SWIPE_V) bringBackToFront();
    },
    [reduceMotion, sendFrontToBack, bringBackToFront],
  );

  const stackKeydown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      bringBackToFront();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      sendFrontToBack();
    }
  };

  return (
    <section className="container-chisme flex flex-col gap-10 py-10 lg:flex-row lg:items-start lg:gap-24 lg:py-16">
      <div className="max-w-xl shrink-0 lg:max-w-[36rem]">
        <h2 className="font-instrument text-4xl leading-tight tracking-[-0.02em] text-[#2a0f2a] sm:text-5xl lg:text-[72px] lg:leading-[0.98] lg:tracking-[-0.025em]">
          <span className="block">{c.h2a}</span>
          <span className="block italic text-chisme-primary">{c.h2b}</span>
        </h2>
        <p className="mt-4 font-jakarta text-base leading-relaxed text-[#5a3158] lg:mt-3 lg:text-lg lg:leading-[27px]">
          {c.intro}
        </p>
      </div>

      <div className="mx-auto w-full max-w-md px-4 pb-10 pt-3 sm:px-5 lg:mx-0 lg:max-w-[452px]">
        <div
          className="relative outline-none"
          role="region"
          aria-label={c.stackRegionAria}
          tabIndex={0}
          onKeyDown={stackKeydown}
        >
          <div
            className="relative min-h-0 overscroll-contain"
            style={{
              minHeight:
                cardContentMinPx != null ? cardContentMinPx + (n - 1) * STACK_STEP_Y + 24 : undefined,
            }}
          >
            <div
              ref={measureRef}
              className="pointer-events-none invisible absolute left-0 top-0 -z-10 w-full"
              aria-hidden
            >
              {threads.map((t, i) => (
                <div key={`m-${i}`} className="w-full">
                  <ChatThreadCard thread={t} threadIdx={i} />
                </div>
              ))}
            </div>
            {paintOrder.map((threadIdx) => {
              const thread = threads[threadIdx];
              const depth = order.indexOf(threadIdx);
              const isFront = depth === 0;
              return (
                <motion.div
                  key={threadIdx}
                  className={
                    isFront
                      ? 'relative w-full cursor-grab touch-pan-x active:cursor-grabbing will-change-transform'
                      : 'pointer-events-none absolute inset-x-0 top-0 w-full'
                  }
                  style={{ zIndex: 20 - depth }}
                  initial={false}
                  animate={{
                    y: depth * STACK_STEP_Y,
                    scale: 1 - depth * 0.022,
                    opacity: 1 - depth * 0.045,
                  }}
                  transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                  drag={isFront && !reduceMotion ? 'x' : false}
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.45}
                  onDragEnd={(e, info) => onDragEnd(e, info, depth)}
                >
                  <ChatThreadCard
                    thread={thread}
                    threadIdx={threadIdx}
                    contentMinHeightPx={cardContentMinPx}
                  />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeRedesignChat;
